import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Icon, Button } from '../../../components/common';
import SEO from '../../../components/common/SEO';

export default function About() {
  const { t } = useTranslation();

  const values = [
    {
      icon: 'clock',
      title: 'Punctuality',
      subtitle: t('info.about_value_punctuality_subtitle'),
      description: t('info.about_value_punctuality_desc'),
    },
    {
      icon: 'person-shield',
      title: 'Safety First',
      subtitle: t('info.about_value_safety_subtitle'),
      description: t('info.about_value_safety_desc'),
    },
    {
      icon: 'lock',
      title: 'Privacy',
      subtitle: t('info.about_value_privacy_subtitle'),
      description: t('info.about_value_privacy_desc'),
    },
  ];

  const services = [
    {
      icon: 'van',
      title: t('nav.private_transfer'),
      en: 'Private Transfer',
      description: t('info.about_service_transfer_desc'),
    },
    {
      icon: 'place',
      title: t('nav.private_tour'),
      en: 'Private Tour',
      description: t('info.about_service_tour_desc'),
    },
    {
      icon: 'shopping',
      title: t('nav.private_shopping'),
      en: 'Private Shopping',
      description: t('info.about_service_shopping_desc'),
    },
  ];

  return (
    <div className="bg-white min-h-screen">
      <SEO route="/about" renderH1 />
      {/* 브랜드 대표 이미지 — VIP Van Tour 프리미엄 밴 + 서울 전경 */}
      <div className="max-w-container mx-auto px-4 sm:px-5 pt-4 sm:pt-6">
        {/* #280·#282: 모바일(1080×810, 4:3)·PC(1920×640, 3:1) 분리 소스. <picture>로
            뷰포트별 1장만 다운로드, source width/height 로 비율별 CLS 예약. */}
        <picture>
          <source
            media="(min-width: 640px)"
            srcSet="/images/logo/vip-van-tour-premium-van-pc.webp"
            width="1920"
            height="640"
          />
          <img
            src="/images/logo/vip-van-tour-premium-van-mobile.webp"
            width="1080"
            height="810"
            alt="VIP Van Tour 프리미엄 스타리아 밴과 서울 전경"
            className="w-full h-auto rounded-2xl"
          />
        </picture>
      </div>
      {/* Hero - Dark Navy + 메인페이지와 동일 폰트 계층 */}
      <div className="bg-gradient-to-br from-blue-900 to-blue-800 py-16 sm:py-20 px-5">
        <div className="flex flex-col items-center text-center gap-5 sm:gap-7">
          {/* 상단 카피 — 점진적 크기 상승 */}
          <div className="flex flex-col items-center gap-1.5 sm:gap-2">
            <p className="text-sm sm:text-base font-medium text-white/60 tracking-wide break-keep">
              {t('transfer_landing.copy_line1')}
            </p>
            <p className="text-base sm:text-lg font-semibold text-white/80 tracking-wide break-keep">
              {t('transfer_landing.copy_line2')}
            </p>
            <p className="text-lg sm:text-2xl font-bold text-white leading-snug break-keep">
              {t('transfer_landing.copy_line3')}
            </p>
          </div>

          {/* 구분선 */}
          <div className="w-12 border-t-2 border-orange-400" />

          {/* Row 43: 브랜드 로고 이미지 (다크 네이비 배경 → brightness-0 invert로 흰색 반전).
              #190 SEO: H1 은 SEO renderH1(페이지 키워드)이 담당 — 로고에 h1 사용 금지 정책 준수. */}
          <img src="/images/logo/logo2.png" alt="VIP VAN TOUR" className="h-10 sm:h-14 w-auto brightness-0 invert" />

          {/* 슬로건 */}
          <p className="text-sm sm:text-lg font-semibold text-orange-300 italic tracking-wide">
            {t('transfer_landing.tagline')}
          </p>
        </div>
      </div>

      {/* ===== OUR VALUE — Row 24: OUR SERVICES와 사이즈/그라디에이션 균형 ===== */}
      <div className="max-w-[800px] mx-auto px-5 py-10 sm:py-16">
        <div className="text-center mb-6 sm:mb-8">
          <p className="text-sm font-bold text-orange-500 tracking-[0.1em] leading-[160%]">{t('info.about_value_label')}</p>
          <h2 className="text-2xl sm:text-3xl font-extrabold text-mono-900 leading-[160%]">{t('info.about_value_title')}</h2>
          <p className="text-sm sm:text-base font-medium text-mono-500 leading-[160%] mt-2 break-keep">{t('info.about_value_desc')}</p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3.5">
          {values.map((item) => (
            <div
              key={item.title}
              className="bg-gradient-to-br from-orange-50 to-orange-100/60 rounded-xl p-5 flex flex-col items-center gap-[13px] border border-orange-100/60 shadow-sm transition-all duration-300 hover:-translate-y-1 hover:shadow-md"
            >
              <div className="w-[52px] h-[52px] bg-white rounded-full flex items-center justify-center shrink-0 shadow-sm">
                <Icon name={item.icon} size={24} className="text-orange-500" />
              </div>
              <h3 className="text-base font-bold text-mono-900 leading-[160%] text-center">{item.subtitle}</h3>
              <p className="text-[11px] font-medium text-mono-400 leading-[160%] text-center -mt-2">{item.title}</p>
              <p className="text-[13px] font-medium text-mono-500 leading-[160%] text-center break-keep whitespace-pre-line">{item.description}</p>
            </div>
          ))}
        </div>
      </div>

      {/* ===== OUR SERVICES — Row 33: 라벨 OUR SERVICES (제공 서비스), OUR VALUE와 균형 디자인 ===== */}
      <div className="bg-gradient-to-br from-blue-900 to-blue-950 py-16 sm:py-20">
        <div className="max-w-[800px] mx-auto px-5">
          <div className="text-center mb-8 sm:mb-10">
            <p className="text-sm font-bold text-orange-500 tracking-[0.1em] leading-[160%]">{t('info.about_services_label')}</p>
            <h2 className="text-2xl sm:text-3xl font-extrabold text-white leading-[160%]">{t('info.about_services_title')}</h2>
            <p className="text-sm sm:text-base text-white/60 leading-[160%] mt-2 break-keep">
              {t('info.about_services_desc')}
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3.5">
            {services.map((item) => (
              <div
                key={item.title}
                className="group bg-gradient-to-br from-blue-800 to-blue-900 rounded-xl p-6 flex flex-col items-center text-center gap-[13px] border border-white/5 shadow-md transition-all duration-300 hover:-translate-y-1 hover:shadow-xl hover:from-blue-700 hover:to-blue-900"
              >
                <div className="w-[56px] h-[56px] bg-white/10 group-hover:bg-orange-500/20 rounded-full flex items-center justify-center shrink-0 transition-colors">
                  <Icon name={item.icon} size={26} className="text-orange-400 group-hover:text-orange-300 transition-colors" />
                </div>
                <h3 className="text-base font-bold text-orange-400 group-hover:text-orange-300 leading-[160%] text-center transition-colors">{item.title}</h3>
                <p className="text-[11px] font-medium text-white/40 leading-[160%] -mt-2 text-center">{item.en}</p>
                <p className="text-[13px] font-medium text-white/70 leading-[160%] text-center break-keep">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* CTA Bar */}
      <div className="bg-mono-50 py-10">
        <div className="max-w-[800px] mx-auto px-5 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-sm font-bold text-mono-900 text-center sm:text-left break-keep">
            {t('info.about_cta')}
          </p>
          <Link to="/partnership">
            <Button level="level1">{t('info.about_cta_button')}</Button>
          </Link>
        </div>
      </div>
    </div>
  );
}
